import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";

export function useLocks(sessionId, editEntry, onDataChanged, onNotification) {
  const [rowLocks, setRowLocks] = useState({});
  const currentLockRef = useRef(null);
  const lastVersionRef = useRef(null);
  const lastNotifTimeRef = useRef(null);
  const seenNotifRef = useRef(new Set());
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const pollUpdates = useCallback(async () => {
    try {
      const requests = [
        axios.get(`${API}/data/locks?session_id=${sessionId}`),
        axios.get(`${API}/data/version?session_id=${sessionId}`),
      ];
      // Only poll notifications after we have a baseline timestamp
      if (lastNotifTimeRef.current) {
        requests.push(axios.get(`${API}/notifications?since=${lastNotifTimeRef.current}&session_id=${sessionId}`));
      } else {
        // First poll: just get current server time as baseline
        requests.push(axios.get(`${API}/notifications?session_id=${sessionId}`));
      }

      const [locksRes, versionRes, notifRes] = await Promise.all(requests);

      setRowLocks(locksRes.data.locks || {});

      const newVersion = versionRes.data.last_updated;
      if (lastVersionRef.current !== null && newVersion !== lastVersionRef.current && onDataChanged) {
        onDataChanged();
      }
      lastVersionRef.current = newVersion;

      const notifications = notifRes.data.notifications || [];
      if (!lastNotifTimeRef.current) {
        // First poll: set baseline, don't show any toasts
        lastNotifTimeRef.current = new Date().toISOString();
        return;
      }

      if (notifications.length > 0) {
        lastNotifTimeRef.current = notifications[notifications.length - 1].timestamp;
        for (const n of notifications) {
          const key = n.timestamp + n.message;
          if (!seenNotifRef.current.has(key)) {
            seenNotifRef.current.add(key);
            if (onNotificationRef.current) onNotificationRef.current(n);
          }
        }
        // Keep set from growing too large
        if (seenNotifRef.current.size > 200) {
          const arr = [...seenNotifRef.current];
          seenNotifRef.current = new Set(arr.slice(-100));
        }
      }
    } catch (err) {}
  }, [sessionId, onDataChanged]);

  useEffect(() => {
    pollUpdates();
    const interval = setInterval(pollUpdates, 3000);
    return () => clearInterval(interval);
  }, [pollUpdates]);

  // Release lock on browser close/refresh via sendBeacon
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentLockRef.current !== null) {
        navigator.sendBeacon(`${API}/data/unlock/${currentLockRef.current}?session_id=${sessionId}`);
        currentLockRef.current = null;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [sessionId]);

  // Refresh lock periodically while edit dialog is open (uses row _id)
  useEffect(() => {
    if (editEntry === null) return;
    const rowId = editEntry.rowId;
    if (!rowId) return;
    const interval = setInterval(async () => {
      try {
        await axios.post(`${API}/data/lock/${rowId}?session_id=${sessionId}`);
      } catch (err) {}
    }, 15000);
    return () => clearInterval(interval);
  }, [editEntry, sessionId]);

  const acquireLock = async (rowId) => {
    try {
      const res = await axios.post(`${API}/data/lock/${rowId}?session_id=${sessionId}`);
      if (!res.data.success) {
        alert(`Gesperrt: ${res.data.message}`);
        return false;
      }
      currentLockRef.current = rowId;
      await pollUpdates();
      return true;
    } catch (err) {
      return false;
    }
  };

  const releaseLock = async (rowId) => {
    try {
      await axios.post(`${API}/data/unlock/${rowId}?session_id=${sessionId}`);
      if (currentLockRef.current === rowId) currentLockRef.current = null;
      await pollUpdates();
    } catch (err) {}
  };

  const syncVersion = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/data/version?session_id=${sessionId}`);
      lastVersionRef.current = res.data.last_updated;
    } catch (err) {}
  }, [sessionId]);

  return { rowLocks, acquireLock, releaseLock, syncVersion };
}
